import { Building2, Receipt, Users, Settings, LucideIcon, Megaphone, GraduationCap, FolderOpen, Link2, UserPlus, Wallet, Briefcase, FileSignature } from 'lucide-react';
import { UserRole, PartnerType } from '@/types';

export interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  children?: NavItem[];
}

export interface NavGroup {
  id: string;
  label: string;
  icon?: LucideIcon;
  children: NavItem[];
}

export type NavConfig = (NavItem | NavGroup)[];

/** Where an admin lands after login and when the logo is clicked. The Requests page stays at /admin. */
export const ADMIN_HOME = '/admin/partners';

/**
 * Get navigation items based on user role and, for partners, partner type.
 */
export function getNavItems(role: UserRole, partnerType?: PartnerType | null): NavConfig {
  if (role === 'admin') {
    return [
      { id: 'partners', label: 'Partners', path: '/admin/partners', icon: Briefcase },
      { id: 'commissions', label: 'Commissions', path: '/admin/commissions', icon: Wallet },
      { id: 'requests', label: 'Requests', path: '/admin', icon: Building2 },
      { id: 'users', label: 'Users', path: '/admin/users', icon: Users },
      { id: 'billing', label: 'Reseller Billing', path: '/admin/billing', icon: Settings },
      { id: 'assets', label: 'Assets', path: '/admin/assets', icon: FolderOpen },
    ];
  }

  // Affiliate partner navigation (earnings arrive in the commissions phase)
  if (partnerType === 'affiliate') {
    return [
      { id: 'referrals', label: 'Referrals', path: '/', icon: UserPlus },
      { id: 'toolkit', label: 'Referral Toolkit', path: '/toolkit', icon: Link2 },
      { id: 'earnings', label: 'Earnings', path: '/earnings', icon: Wallet },
      { id: 'sales-marketing', label: 'Sales & Marketing', path: '/sales-marketing', icon: Megaphone },
      { id: 'training', label: 'Training', path: '/training', icon: GraduationCap },
      { id: 'agreement', label: 'Agreement', path: '/agreement', icon: FileSignature },
    ];
  }

  // Reseller partner navigation
  return [
    { id: 'accounts', label: 'Accounts', path: '/', icon: Building2 },
    { id: 'billing', label: 'Billing', path: '/billing', icon: Receipt },
    { id: 'sales-marketing', label: 'Sales & Marketing', path: '/sales-marketing', icon: Megaphone },
    { id: 'training', label: 'Training', path: '/training', icon: GraduationCap },
    { id: 'agreement', label: 'Agreement', path: '/agreement', icon: FileSignature },
  ];
}

/**
 * Check if a path is active based on the current location
 *
 * Rules:
 * - Exact match: /admin matches /admin
 * - Prefix match with slash: /admin/users matches /admin/users/123
 * - Root paths (/ and /admin) only match exactly to avoid over-matching
 */
export function isPathActive(itemPath: string, currentPath: string): boolean {
  // Normalize paths
  const normalizedItemPath = itemPath.endsWith('/') ? itemPath.slice(0, -1) : itemPath;
  const normalizedCurrentPath = currentPath.endsWith('/') ? currentPath.slice(0, -1) : currentPath;

  // Exact match
  if (normalizedCurrentPath === normalizedItemPath) {
    return true;
  }

  // For root paths, only allow exact match
  if (normalizedItemPath === '' || normalizedItemPath === '/admin') {
    return false;
  }

  // Prefix match: current path starts with item path + /
  return normalizedCurrentPath.startsWith(normalizedItemPath + '/');
}

/**
 * Check if a nav group has an active child
 */
export function isGroupActive(group: NavGroup, currentPath: string): boolean {
  return group.children.some(child => isPathActive(child.path, currentPath));
}

/**
 * Type guard to check if a nav config item is a group
 */
export function isNavGroup(item: NavItem | NavGroup): item is NavGroup {
  return 'children' in item && !('path' in item);
}

/**
 * Sidebar width constant
 */
export const DRAWER_WIDTH = 260;
